import { CognitoUserInterface } from "@aws-amplify/ui-components";
import { Frame, Toast } from "@shopify/polaris";
import React, { useState, ReactNode, useCallback, useEffect } from "react";
import MainFrameContextType from "../../types/MainFrameContextType";
import Header from "../Header";
import { ToastContainer } from "react-toastify";
import SideBar from "../Navigation/SideBar";
import Footer from "../Footer/Footer";
import { CookieConsent } from "../CookieConsent/CookieConsent";
import { useCookies } from "react-cookie";
import { cookiePreferences, COOKIE_CONSENT } from "../../helpers/cookieUtils";
import { CookieDialog } from "../CookieDialog/CookieDialog";
import HotJarConfig from "../../config/HotJarConfig";
import { hotjar } from "react-hotjar";

let hotjarInitialised = false;

export const MainFrameContext =
    React.createContext<MainFrameContextType | null>(null);

interface IProps {
    children: ReactNode;
    user: CognitoUserInterface;
}

export default function MainFrameContextProvider({ children, user }: IProps) {
    const [showSideBar, setShowSideBar] = useState(false);
    const [showTopBar, setShowTopBar] = useState(true);
    const [mobileNavigationActive, setMobileNavigationActive] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastType, setToastType] = useState("");
    const [anchor, setAnchor] = useState("#");
    const [allowDelete, setAllowDelete] = useState(false);

    const [cookies, setCookie] = useCookies([COOKIE_CONSENT]);
    const cookieUtils = cookiePreferences(setCookie, cookies);

    const [showCookieConsentDialog, setShowCookieConsentDialog] = useState(false);
    const storedConsentCookies = cookieUtils.readCookiePreferences();

    const noCookiesSet =
        storedConsentCookies?.all === undefined &&
        storedConsentCookies?.nonEssential === undefined &&
        storedConsentCookies?.analytics === undefined &&
        !window.location.pathname.includes("/logout");

    const [cookiePopupVisible, setCookiePopupVisible] = useState<boolean>();
    const [analyticsCookiesEnabled, setAnalyticsCookiesEnabled] = useState(false);

    useEffect(() => {
        setAnalyticsCookiesEnabled(Boolean(storedConsentCookies?.analytics));
        setCookiePopupVisible(noCookiesSet);
    }, [storedConsentCookies]);

    if (
        !hotjarInitialised &&
          (cookies[COOKIE_CONSENT]?.all === true || cookies[COOKIE_CONSENT]?.analytics === true)
    ) {
        if (HotJarConfig.trackingId !== 0) {
            console.info("Initialising Hotjar");
            hotjar.initialize(HotJarConfig.trackingId, HotJarConfig.version);
            hotjarInitialised = true;
        } else {
            console.info("Cookies enabled but HotJar analytics not configured for this environment");
        }
    }


    /**
     * Create a toast at the bottom of the app
     * @param toastType error or noraml
     * @param toastMessage  what text to display
     */
    function createToast(toastType: string, toastMessage: string) {
        setShowToast(true);
        setToastType(toastType);
        setToastMessage(toastMessage);
    }

    /**
     * Tsx component to display either error toast or default toast
     */
    const AppBasicToast = showToast ? (
        <div>
            {toastType === "Error" ? (
                <Toast
                    content={toastMessage}
                    error
                    onDismiss={() => {
                        setShowToast(false);
                    }}
                />
            ) : (
                <Toast
                    content={toastMessage}
                    onDismiss={() => {
                        setShowToast(false);
                    }}
                />
            )}
        </div>
    ) : null;

    const toggleMobileNavigationActive = useCallback(
        () =>
            setMobileNavigationActive(
                (mobileNavigationActive) => !mobileNavigationActive
            ),
        []
    );

    return (
        <>
            <MainFrameContext.Provider
                value={{
                    setShowSideBar,
                    setShowTopBar,
                    setMobileNavigationActive,
                    createToast,
                    toggleMobileNavigationActive,
                    anchor,
                    setAnchor,
                    allowDelete,
                    setAllowDelete
                }}
            >
                <Frame
                    topBar={showTopBar ? <Header user={user} /> : null}
                    navigation={showSideBar ? <SideBar/> : null}
                    showMobileNavigation={mobileNavigationActive}
                    onNavigationDismiss={toggleMobileNavigationActive}
                >
                    {children}
                    {AppBasicToast}
                    <ToastContainer />
                    <Footer 
                        showCookieConsent={true}
                        onClickCookiePolicy={() => setShowCookieConsentDialog(true)}
                    />
                    <CookieDialog
                        active={showCookieConsentDialog}
                        dataPrivacyLink={"mailto:Privacy@Idwalmarine.com"}
                        dataPrivacyLinkText={"Privacy@Idwalmarine.com"}
                        analyticCookiesEnabled={analyticsCookiesEnabled} 
                        onClose={() => setShowCookieConsentDialog(false)}   
                        onAnalyticsCookieToggle={() => {
                            cookieUtils.acceptAnalytics(
                                !analyticsCookiesEnabled
                            );
                            setAnalyticsCookiesEnabled(
                                !analyticsCookiesEnabled
                            );
                        }}          
                    />
                </Frame>
            </MainFrameContext.Provider>
            {
                (Boolean(cookiePopupVisible)) && (
                    <CookieConsent 
                        onAcceptAll={() => cookieUtils.acceptAll()}
                        onRejectAll={() => cookieUtils.rejectAll()}
                        onClickCookiePolicy={() => setShowCookieConsentDialog(true)}
                    />
                )
            }
        </>
    );
}
